import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PageCard } from '../layout/page/PageCard';
import { ActiveStatusBadge } from '../ui/ActiveStatusBadge';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { EditIconButton } from '../ui/EditIconButton';
import { InlineAlert } from '../ui/InlineAlert';
import { RoundedCheckbox } from '../ui/RoundedCheckbox';
import { SortOrderControls } from '../ui/SortOrderControls';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  getProductCapabilities,
  SalesProductCapabilityLink,
} from '../../services/salesCapabilitiesApi';
import {
  BillingInterval,
  getProductPrices,
  replaceProductPrices,
  SalesProductPrice,
  SalesProductPriceItemPayload,
} from '../../services/productPricesApi';
import { CatalogMultiSelectModal, CatalogSelectItem } from './CatalogMultiSelectModal';

type ProductPricesPanelProps = {
  productId: string;
  readOnly?: boolean;
};

type DraftPrice = SalesProductPriceItemPayload & {
  localKey: string;
  capabilitySelections: Record<string, number>;
  isNew?: boolean;
};

const BILLING_INTERVAL_OPTIONS: { value: BillingInterval; label: string }[] = [
  { value: 'Monthly', label: 'Mensal' },
  { value: 'Yearly', label: 'Anual' },
];

function createEmptyPrice(sortOrder: number): DraftPrice {
  return {
    localKey: crypto.randomUUID(),
    name: '',
    currency: 'BRL',
    amount: 0,
    billingInterval: 'Monthly',
    isActive: true,
    sortOrder,
    capabilities: [],
    capabilitySelections: {},
    isNew: true,
  };
}

function mapPriceToDraft(price: SalesProductPrice): DraftPrice {
  const capabilitySelections: Record<string, number> = {};
  for (const capability of price.capabilities) {
    capabilitySelections[capability.capabilityId] = capability.includedQuantity;
  }

  return {
    localKey: price.id,
    name: price.name ?? '',
    currency: price.currency,
    amount: price.amount,
    billingInterval: price.billingInterval,
    isActive: price.isActive,
    sortOrder: price.sortOrder,
    capabilities: price.capabilities.map((capability) => ({
      capabilityId: capability.capabilityId,
      includedQuantity: capability.includedQuantity,
    })),
    capabilitySelections,
    isNew: false,
  };
}

function formatAmount(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function intervalLabel(value?: BillingInterval): string {
  return BILLING_INTERVAL_OPTIONS.find((item) => item.value === value)?.label ?? 'Mensal';
}

export function ProductPricesPanel({ productId, readOnly = false }: ProductPricesPanelProps) {
  const { canWriteModule } = usePermissions();
  const canWrite = !readOnly && canWriteModule('sales.products');
  const [productCapabilities, setProductCapabilities] = useState<SalesProductCapabilityLink[]>([]);
  const [prices, setPrices] = useState<DraftPrice[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [capabilityModalOpen, setCapabilityModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const [capabilities, productPrices] = await Promise.all([
          getProductCapabilities(productId),
          getProductPrices(productId),
        ]);
        setProductCapabilities(capabilities);
        setPrices(productPrices.map(mapPriceToDraft));
        setEditingKey(null);
      } catch {
        toast.error('Falha ao carregar precificação do produto.');
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  const editingPrice = prices.find((price) => price.localKey === editingKey) ?? null;
  const modalOpen = editingPrice != null;

  const includedCapabilities = useMemo(() => {
    if (!editingPrice) {
      return [];
    }

    return productCapabilities.filter(
      (capability) => editingPrice.capabilitySelections[capability.capabilityId] != null,
    );
  }, [editingPrice, productCapabilities]);

  const includedCapabilityIds = useMemo(
    () => includedCapabilities.map((capability) => capability.capabilityId),
    [includedCapabilities],
  );

  function updatePrice(localKey: string, patch: Partial<DraftPrice>): void {
    if (!canWrite) return;
    setPrices((current) =>
      current.map((price) => (price.localKey === localKey ? { ...price, ...patch } : price)),
    );
  }

  function removeCapability(localKey: string, capabilityId: string): void {
    if (!canWrite) return;

    setPrices((current) =>
      current.map((price) => {
        if (price.localKey !== localKey) {
          return price;
        }

        const nextSelections = { ...price.capabilitySelections };
        delete nextSelections[capabilityId];
        return { ...price, capabilitySelections: nextSelections };
      }),
    );
  }

  function updateCapabilityQuantity(localKey: string, capabilityId: string, quantity: number): void {
    if (!canWrite) return;

    setPrices((current) =>
      current.map((price) => {
        if (price.localKey !== localKey) {
          return price;
        }

        return {
          ...price,
          capabilitySelections: {
            ...price.capabilitySelections,
            [capabilityId]: Math.max(1, quantity),
          },
        };
      }),
    );
  }

  function handleAddCapabilities(selected: CatalogSelectItem[]): void {
    if (!canWrite || !editingKey || selected.length === 0) return;

    setPrices((current) =>
      current.map((price) => {
        if (price.localKey !== editingKey) {
          return price;
        }

        const nextSelections = { ...price.capabilitySelections };
        for (const item of selected) {
          if (nextSelections[item.id] == null) {
            const linked = productCapabilities.find((capability) => capability.capabilityId === item.id);
            nextSelections[item.id] = linked?.quantityDefault ?? 1;
          }
        }

        return { ...price, capabilitySelections: nextSelections };
      }),
    );
  }

  function addPrice(): void {
    if (!canWrite) return;
    const next = createEmptyPrice(prices.length);
    setPrices((current) => [...current, next]);
    setEditingKey(next.localKey);
  }

  function closeEditor(discardNew = false): void {
    if (!editingKey) {
      return;
    }

    if (discardNew) {
      setPrices((current) =>
        current.filter((price) => !(price.localKey === editingKey && price.isNew)),
      );
    }

    setCapabilityModalOpen(false);
    setEditingKey(null);
  }

  function removePrice(localKey: string): void {
    if (!canWrite) return;
    setPrices((current) => current.filter((price) => price.localKey !== localKey));
    if (editingKey === localKey) {
      setEditingKey(null);
    }
  }

  function movePrice(index: number, direction: 'up' | 'down'): void {
    if (!canWrite) return;

    setPrices((current) => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= current.length) {
        return current;
      }

      const next = [...current];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  }

  async function handleSave(): Promise<void> {
    if (!canWrite) return;

    setSaving(true);
    try {
      const items: SalesProductPriceItemPayload[] = prices.map((price, index) => ({
        name: price.name?.trim() || `Plano ${index + 1}`,
        currency: price.currency ?? 'BRL',
        amount: Number(price.amount) || 0,
        billingInterval: price.billingInterval ?? 'Monthly',
        isActive: price.isActive ?? true,
        sortOrder: index,
        capabilities: Object.entries(price.capabilitySelections).map(([capabilityId, includedQuantity]) => ({
          capabilityId,
          includedQuantity,
        })),
      }));
      await replaceProductPrices(productId, items);
      toast.success('Precificação do produto atualizada.');
      const refreshed = await getProductPrices(productId);
      setPrices(refreshed.map(mapPriceToDraft));
      setEditingKey(null);
    } catch {
      toast.error('Falha ao salvar precificação.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-secondary-500">Carregando precificação...</p>;
  }

  return (
    <PageCard title="Precificação" subtitle="Planos de preço com capabilities incluídas.">
      {productCapabilities.length === 0 ? (
        <InlineAlert tone="warning" className="mb-4">
          Vincule capabilities na aba Capabilities antes de detalhar os planos.
        </InlineAlert>
      ) : null}

      {canWrite ? (
        <div className="mb-4 flex justify-end">
          <button type="button" className="erp-btn-secondary" onClick={addPrice}>
            Adicionar preço
          </button>
        </div>
      ) : null}

      {prices.length === 0 ? (
        <p className="text-secondary-500">Nenhum preço cadastrado para este produto.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-secondary-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                <th className="px-3 py-2 font-medium">Modalidade</th>
                <th className="px-3 py-2 font-medium">Valor</th>
                <th className="px-3 py-2 font-medium">Intervalo</th>
                <th className="px-3 py-2 font-medium">Capabilities</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {prices.map((price, index) => {
                const capabilityCount = Object.keys(price.capabilitySelections).length;

                return (
                  <tr key={price.localKey} className="bg-white">
                    <td className="px-3 py-2 font-medium text-secondary-900">
                      {price.name?.trim() || `Plano ${index + 1}`}
                    </td>
                    <td className="px-3 py-2 font-mono text-secondary-800">
                      {price.currency ?? 'BRL'} {formatAmount(Number(price.amount) || 0)}
                    </td>
                    <td className="px-3 py-2">{intervalLabel(price.billingInterval)}</td>
                    <td className="px-3 py-2 text-secondary-600">{capabilityCount}</td>
                    <td className="px-3 py-2">
                      <ActiveStatusBadge active={price.isActive ?? true} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {canWrite ? (
                          <SortOrderControls
                            canMoveUp={index > 0}
                            canMoveDown={index < prices.length - 1}
                            onMoveUp={() => movePrice(index, 'up')}
                            onMoveDown={() => movePrice(index, 'down')}
                          />
                        ) : null}
                        <EditIconButton
                          label="Editar"
                          onClick={() => setEditingKey(price.localKey)}
                        />
                        {canWrite ? (
                          <DeleteIconButton
                            label="Excluir"
                            onClick={() => removePrice(price.localKey)}
                          />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canWrite ? (
        <div className="erp-btn-actions mt-4">
          <button type="button" className="erp-btn-secondary" onClick={addPrice}>
            Adicionar preço
          </button>
          <button
            type="button"
            className="erp-btn-primary"
            disabled={saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Salvando...' : 'Salvar precificação'}
          </button>
        </div>
      ) : null}

      <DraggableModalPanel
        open={modalOpen}
        title={editingPrice?.isNew ? 'Novo preço' : 'Editar preço'}
        subtitle={
          editingPrice?.isNew
            ? 'Preencha os dados básicos e as capabilities incluídas.'
            : editingPrice?.name?.trim() || 'Atualize os dados do plano.'
        }
        onClose={() => closeEditor(true)}
        className="w-full max-w-3xl"
        footer={
          <div className="erp-btn-actions">
            <button type="button" className="erp-btn-secondary" onClick={() => closeEditor(true)}>
              Cancelar
            </button>
            <button
              type="button"
              className="erp-btn-primary"
              onClick={() => {
                if (editingPrice?.isNew) {
                  updatePrice(editingPrice.localKey, { isNew: false });
                }
                closeEditor(false);
              }}
            >
              {editingPrice?.isNew ? 'Adicionar à lista' : 'Concluir'}
            </button>
          </div>
        }
      >
        {editingPrice ? (
          <div className="space-y-4">
            <div className="erp-form-grid">
              <label className="erp-label">
                Modalidade
                <input
                  className="erp-input"
                  maxLength={120}
                  value={editingPrice.name ?? ''}
                  disabled={!canWrite}
                  placeholder="Ex.: Básico, Essencial..."
                  onChange={(event) =>
                    updatePrice(editingPrice.localKey, { name: event.target.value })
                  }
                />
              </label>
              <label className="erp-label">
                Moeda
                <input
                  className="erp-input font-mono uppercase"
                  maxLength={3}
                  value={editingPrice.currency ?? 'BRL'}
                  disabled={!canWrite}
                  onChange={(event) =>
                    updatePrice(editingPrice.localKey, {
                      currency: event.target.value.toUpperCase(),
                    })
                  }
                />
              </label>
              <label className="erp-label">
                Valor
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  className="erp-input"
                  value={editingPrice.amount}
                  disabled={!canWrite}
                  onChange={(event) =>
                    updatePrice(editingPrice.localKey, { amount: Number(event.target.value) })
                  }
                />
              </label>
              <label className="erp-label">
                Intervalo
                <select
                  className="erp-input"
                  value={editingPrice.billingInterval ?? 'Monthly'}
                  disabled={!canWrite}
                  onChange={(event) =>
                    updatePrice(editingPrice.localKey, {
                      billingInterval: event.target.value as BillingInterval,
                    })
                  }
                >
                  {BILLING_INTERVAL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label-inline mt-6">
                <RoundedCheckbox
                  checked={editingPrice.isActive ?? true}
                  disabled={!canWrite}
                  onChange={(event) =>
                    updatePrice(editingPrice.localKey, { isActive: event.target.checked })
                  }
                />
                Ativo
              </label>
            </div>

            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-secondary-800">Capabilities incluídas</p>
                {canWrite && productCapabilities.length > 0 ? (
                  <button
                    type="button"
                    className="erp-btn-secondary erp-btn-sm"
                    onClick={() => setCapabilityModalOpen(true)}
                  >
                    Adicionar capabilities
                  </button>
                ) : null}
              </div>

              {includedCapabilities.length === 0 ? (
                <InlineAlert>
                  Nenhuma capability incluída neste preço.
                  {productCapabilities.length === 0
                    ? ' Vincule capabilities ao produto primeiro.'
                    : ' Use “Adicionar capabilities”.'}
                </InlineAlert>
              ) : (
                <ul className="space-y-2">
                  {includedCapabilities.map((capability) => {
                    const quantity =
                      editingPrice.capabilitySelections[capability.capabilityId] ?? 1;

                    return (
                      <li
                        key={capability.capabilityId}
                        className="rounded-lg border border-secondary-200 bg-white px-3 py-2"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium">{capability.name}</span>
                            <label className="erp-label-inline mt-2 text-sm">
                              Quantidade incluída
                              <input
                                type="number"
                                min={1}
                                className="erp-input ml-2 w-24"
                                disabled={!canWrite}
                                value={quantity}
                                onChange={(event) =>
                                  updateCapabilityQuantity(
                                    editingPrice.localKey,
                                    capability.capabilityId,
                                    Number(event.target.value),
                                  )
                                }
                              />
                            </label>
                          </div>
                          {canWrite ? (
                            <DeleteIconButton
                              label="Remover"
                              onClick={() =>
                                removeCapability(editingPrice.localKey, capability.capabilityId)
                              }
                            />
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </DraggableModalPanel>

      <CatalogMultiSelectModal
        open={capabilityModalOpen}
        title="Adicionar capabilities ao preço"
        subtitle="Somente capabilities já vinculadas ao produto."
        alreadySelectedIds={includedCapabilityIds}
        fetchPage={async (page, pageSize, search) => {
          const term = search.trim().toLowerCase();
          const filtered = productCapabilities.filter((capability) => {
            if (!term) {
              return true;
            }

            return capability.name.toLowerCase().includes(term);
          });

          const start = (Math.max(1, page) - 1) * pageSize;
          const slice = filtered.slice(start, start + pageSize);

          return {
            items: slice.map((capability) => ({
              id: capability.capabilityId,
              title: capability.name,
            })),
            totalCount: filtered.length,
            page: Math.max(1, page),
            pageSize,
          };
        }}
        onClose={() => setCapabilityModalOpen(false)}
        onConfirm={handleAddCapabilities}
      />
    </PageCard>
  );
}
